import React from "react";
import { Card } from "../../Card";
import moment from "moment";
import { formatMoney } from "../../../configs/formatCurrency";
import "./ViewLoanPayments.css";
import StatusBadge from "../../StatusBadge";

const ViewLoanPayments = ({
  loanData,
  handleOpenModal,
  handleDeleteInterest,
}) => {
  const totalPayable = loanData?.amount_payable;
  const totalPaid = (loanData?.total_repayments || 0) + (loanData?.pre_onboarding_payment || 0);
  const calculateProgress = () => {
    if (!loanData) return 0;
    return totalPayable > 0 ? Math.min(100, (totalPaid / totalPayable) * 100) : 0;
  };
  
  const progressPercentage = calculateProgress();
  const isFullyPaid = progressPercentage >= 100;
  const endDate = moment(loanData?.start_date).add(loanData?.duration, loanData?.duration_unit);
  const timeRemaining = moment(endDate).diff(moment(), 'months');
  
  return (
    <div className="loan-payment-container">
      <Card className="loan-info-card">
        <div className="card-title">
          <div className="title-section">
            <i className="fa fa-info-circle title-icon"></i>
            <span>Loan Information</span>
          </div>
          
          {loanData?.status === 'disbursed' && (
            <div className="loan-actions">
              <button
                className="action-button top-up-button"
                onClick={() => handleOpenModal('showTopUpModal', loanData)}
            >
              <i className="fa fa-plus-circle"></i>
              <span className="top-up-text"
              style={{
                color: '#fff',
              }}
              >
                Top Up Loan
              </span>
            </button>
            </div>
          )}
        </div>
        
        <div className="loan-details-container">
          {/* Loan Summary Card */}
          <div className="details-card">
            <div className="card-header">
              <h4 className="card-title">Loan Details</h4>
              <div className=''>
                <StatusBadge status={loanData?.status || 'info'} type='loan' />
              </div>
            </div>

            <div className="summary-content">
              <div className="details-row">
                <div className="detail-group">
                  <label>Loan ID</label>
                  <div className="value">{loanData?.loan_id ?? '-'}</div>
                </div>
                <div className="detail-group">
                  <label>Loan Product</label>
                  <div className="value">{loanData?.loan_product?.name?.toUpperCase() || "General"}</div>
                </div>
                <div className="detail-group">
                  <label>Member</label>
                  <div className="value">{loanData?.member?.user_name?.toUpperCase() ?? '-'}</div>
                </div>
                <div className="detail-group">
                  <label>Member No</label>
                  <div className="value">{loanData?.member?.member_no ?? '-'}</div>
                </div>
              </div>

              <div className="details-row">
                <div className="detail-group">
                  <label>Created</label>
                  <div className="value">{moment(loanData?.created_at).format("MMM DD, YYYY")}</div>
                </div>
                <div className="detail-group">
                  <label>Start Date</label>
                  <div className="value">
                    {loanData?.start_date ? moment(loanData?.start_date).format("MMM DD, YYYY") : '-'}
                  </div>
                </div>
                <div className="detail-group">
                  <label>Duration</label>
                  <div className="value">{`${loanData?.duration} (${loanData?.duration_unit})`}</div>
                </div>
                <div className="detail-group">
                  <label>Interest Rate</label>
                  <div className="value">
                  {
                    `${loanData?.interest_rate}% (${loanData?.interest_frequency}) ${(loanData?.amortization_method?.toUpperCase() ?? '-').replace('_', ' ')}`
                  }</div>
                </div>
              </div>
            </div>
            <hr/>
            <div className="financial-content">
              <div className="details-row">
                <div className="detail-group">
                  <label>Principal</label>
                  <div className="value">KES {formatMoney(loanData?.principal_amount || 0)}</div>
                </div>
                <div className="detail-group">
                  <label>Interest</label>
                  <div className="value">KES {formatMoney(loanData?.total_interest || 0)}</div>
                </div>
                <div className="detail-group">
                  <label>Fees</label>
                  <div className="value">KES {formatMoney(loanData?.total_fees || 0)}</div>
                </div>
                <div className="detail-group">
                  <label>Top Ups</label>
                  <div className="value">KES {formatMoney(loanData?.total_loan_topups || 0)}</div>
                </div>
              </div>

              <div className="details-row">
                <div className="detail-group">
                  <label>Amount Payable</label>
                  <div className="value highlight">KES {formatMoney(totalPayable)}</div>
                </div>
                <div className="detail-group">
                  <label>Onboarding Payment</label>
                  <div className="value">KES {formatMoney(loanData?.pre_onboarding_payment || 0)}</div>
                </div>
                <div className="detail-group">
                  <label>Total Repayments</label>
                  <div className="value">KES {formatMoney(loanData?.total_repayments || 0)}</div>
                </div>
                <div className="detail-group">
                  <label>Current Balance</label>
                  <div className="value highlight">KES {formatMoney(loanData?.balance || 0)}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Interest Accrued Card */}
          {loanData?.amortization_method === 'reducing_balance' && (
            <div className="details-card">
              <div className="card-header">
                <h4 className="card-title">Accrued Interest</h4>
                <button 
                className="action-button"
                onClick={() => handleOpenModal('showInterestModal', loanData)}
              >
                <i className="fa fa-plus-circle"></i>
                <span>Add Interest</span>
              </button>
            </div>

            <div className="payment-table-container">
              <table className="table table-bordered table-striped table-hover payment-table">
                <thead>
                  <tr>
                    <th>Date</th>
                    <th>Description</th>
                    <th>Principal Amount</th>
                    <th>Interest Amount</th>
                    <th>Created By</th>
                    <th style={{ textAlign: "center" }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loanData?.loan_interests?.length > 0 ? (
                    loanData.loan_interests.map((item, i) => (
                      <tr key={i}>
                        <td>{moment(item.interest_date).format("ll")}</td>
                        <td>{item.description}</td>
                        <td>KES {formatMoney(item.principal_amount)}</td>
                        <td>KES {formatMoney(item.interest_amount)}</td>
                        <td>{item.created_by?.user_name || '-'}</td>
                        <td style={{ textAlign: "center" }}>
                          <button
                            className="btn btn-sm btn-link"
                            onClick={() => handleOpenModal('showInterestModal', loanData, item)}
                          >
                            <i className="fa fa-edit" style={{ color: '#F79222' }}></i>
                          </button>
                          <button
                            className="btn btn-sm btn-link"
                            onClick={() => handleDeleteInterest(item._id)}
                          >
                            <i className="fa fa-trash" style={{ color: '#F9394C' }}></i>
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr className="no-data-row">
                      <td colSpan={6}>
                        <div className="no-data-message">
                          <i className="fa fa-info-circle mr-2"></i>
                          {' No interest records found'}
                        </div>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>)}

          {/* Payment Section */}
          <div className="details-card">
            <div className="card-header">
              <h4 className="card-title">Loan Repayments</h4>
              <button 
                className="action-button"
                onClick={() => handleOpenModal('showTransactionModal', loanData)}
              >
                <i className="fa fa-plus-circle"></i>
                <span>Add Payment</span>
              </button>
            </div>

            <div className="progress-details">
              <div className="progress-text">
                <div>
                  KES {formatMoney(totalPaid)} of KES {formatMoney(totalPayable)}
                </div>
                <div className="progress-status">
                  {isFullyPaid ? (
                    <span className="status-paid">Fully Paid</span>
                  ) : (
                    <span>{Math.round(progressPercentage)}% repaid</span>
                  )}
                </div>
              </div>
              
              <div className="progress-bar-container">
                <div 
                  className="progress-bar" 
                  style={{width: `${progressPercentage}%`}}
                ></div>
              </div>
              
              <div className="days-remaining">
                {isFullyPaid ? (
                  "0 months remaining"
                ) : (
                  timeRemaining ? (
                    <span className={timeRemaining < 0 ? 'overdue-text' : ''}>
                      {`${Math.abs(timeRemaining)} months ${timeRemaining < 0 ? 'overdue' : 'remaining'} `}
                    </span>
                  ) : (
                    "Months remaining: calculating..."
                  )
                )}
              </div>
            </div>

            <div className="payment-table-container">
              <table className="table table-bordered table-striped table-hover payment-table">
                <thead>
                  <tr>
                    <th>Reference</th>
                    <th>Date</th>
                    <th>Amount</th>
                    <th>Loan Balance</th>
                    <th>Vehicle</th>     
                    <th>Cashier</th>
                    <th>Device</th>
                  </tr>
                </thead>
                <tbody>
                {loanData?.repayments?.length > 0 ? (
                  loanData?.repayments.map((item, i) => (
                    <tr key={i}>
                      <td>{item?.loan_allocation?.loan_transaction?.reference_number}</td>
                      <td>{moment(item?.loan_allocation?.loan_transaction?.transaction_date).format("ll")}</td>
                      <td>KES {formatMoney(item?.loan_allocation?.amount)}</td>
                      <td>KES {formatMoney(item?.balance)}</td>
                      <td>{item?.loan_allocation?.loan_transaction?.vehicle_no}</td>
                      <td>{item?.loan_allocation?.loan_transaction?.cashier_name}</td>
                      <td>{item?.loan_allocation?.loan_transaction?.device_name}</td>
                    </tr>
                  ))
                ) : (
                  <tr className="no-data-row">
                    <td colSpan={10}>
                      <div className="no-data-message">
                        <i className="fa fa-info-circle mr-2"></i>
                        {' No payment records found'}
                      </div>
                    </td>
                  </tr>
                )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
        
        {/* Guarantor Information Card */}
        {/* {loanData.guaranted_amount && Object.keys(loanData.guaranted_amount).length > 0 && (
          <div className="guarantor-section">
            <div className="section-header">
              <h4>Guarantor Information</h4>
              <hr />
            </div>
            
            <table className="guarantor-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Guaranteed Amount</th>
                </tr>
              </thead>
              <tbody>
                {Object.keys(loanData.guaranted_amount).map((g, i) => (
                  <tr key={i}>
                    <td>{loanData.guaranted_amount[g].name}</td>
                    <td>KES {formatMoney(loanData.guaranted_amount[g].amount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )} */}
      </Card>
    </div>
  );
};

export default ViewLoanPayments;
